import { useState, useEffect } from 'react';
import { usePage } from '../contexts/PageContext.jsx';
import ConvergenceScorer from '../components/signal-convergence/ConvergenceScorer.jsx';
import CapitulationDetector from '../components/signal-convergence/CapitulationDetector.jsx';
import OpportunityCard from '../components/signal-convergence/OpportunityCard.jsx';
import CongressionalTrades from '../components/signal-convergence/CongressionalTrades.jsx';
import SmartMoneyTracker from '../components/signal-convergence/SmartMoneyTracker.jsx';
import SignalFeed from '../components/signal-convergence/SignalFeed.jsx';
import PortfolioImpactPanel from '../components/PortfolioImpactPanel.jsx';
import TrackRecordPanel from '../components/TrackRecordPanel.jsx';
import { usePageTracking } from '../hooks/usePageTracking.js';

export default function SignalConvergence() {
  const { setPage } = usePage();
  useEffect(() => { setPage('signal-convergence'); }, []);
  const [opportunities, setOpportunities] = useState([]);

  useEffect(() => {
    fetchOpportunities();
    const interval = setInterval(fetchOpportunities, 60000);
    return () => clearInterval(interval);
  }, []);

  async function fetchOpportunities() {
    try {
      const res = await fetch('/api/signals/feed?conviction=HIGH&limit=6');
      if (res.ok) setOpportunities(await res.json());
    } catch { /* silent */ }
  }

  function handleAction(signalId) {
    setOpportunities(prev => prev.map(s =>
      s.id === signalId ? { ...s, user_action: 'ACTED' } : s
    ));
  }

  const { impact, track, loading } = usePageTracking('signals');
  const highCount = opportunities.filter(o => (o.conviction_score || 0) >= 75).length;
  const divergenceCount = opportunities.filter(o => o.divergence || o.is_divergence).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold text-white">Signal Convergence</h1>
        <span className="text-korinth-muted text-sm">Multi-source opportunity detection & scoring</span>
      </div>

      {/* Sprint 20: Summary strip */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg px-4 py-2.5 text-[11px] font-mono text-korinth-text flex flex-wrap items-center gap-4">
        <span><span className="text-korinth-muted">Active opportunities:</span> <span className="text-white font-bold">{opportunities.length}</span></span>
        <span><span className="text-korinth-muted">HIGH conviction:</span> <span className="text-korinth-accent font-bold">{highCount}</span></span>
        <span><span className="text-korinth-muted">Divergences:</span> <span className="text-korinth-warning font-bold">{divergenceCount}</span></span>
      </div>

      {/* Sprint 20: Portfolio impact */}
      <PortfolioImpactPanel topic="Signals" {...impact} loading={loading} />

      <ConvergenceScorer />

      <CapitulationDetector />

      {/* Top Opportunities */}
      {opportunities.length > 0 && (
        <div>
          <h2 className="text-xs font-mono text-korinth-muted uppercase tracking-wider mb-3">
            Top Opportunities ({opportunities.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {opportunities.map(s => (
              <OpportunityCard key={s.id} signal={s} onAction={handleAction} />
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CongressionalTrades />
        <SmartMoneyTracker />
      </div>

      <SignalFeed />

      {/* Sprint 20: Track record */}
      <TrackRecordPanel {...track} loading={loading} metric_name="Signal Convergence" />
    </div>
  );
}
